/**
 * Dashboard — Financial Command Center
 * src/pages/Dashboard.jsx
 *
 * Strategic overview:
 *   • 4 KPI cards: Patrimonio Neto, Ingresos Neto, Balance Mensual, Tasa de Ahorro
 *   • "Panorama 12 Meses" — stacked bar (Gastos + Pasivos) with Ingresos line
 *   • "Balance Proyectado" — area chart showing cumulative savings curve
 *   • Salud Financiera indicators
 */

import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  Legend, ResponsiveContainer, AreaChart, Area, ReferenceLine,
} from 'recharts';

const fmt = (n) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n || 0);
const fmtFull = (n) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2 }).format(n || 0);
const fmtShort = (n) => {
  const v = Math.abs(n || 0);
  if (v >= 1_000_000) return `$${((n||0)/1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${((n||0)/1_000).toFixed(0)}K`;
  return fmt(n);
};

const MONTHS_ES = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
const now = new Date();

// ── Custom tooltip for the main chart ──────────────────────────────────────
const PanoramaTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '0.625rem', padding: '0.75rem 1rem', fontSize: '0.8rem', boxShadow: '0 4px 16px rgba(0,0,0,0.15)', color: 'var(--text-primary)', minWidth: '160px' }}>
      <div style={{ fontWeight: 700, marginBottom: '0.5rem', color: 'var(--text-primary)' }}>{label}</div>
      {payload.map((p, i) => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: '1rem', marginBottom: '0.2rem' }}>
          <span style={{ color: p.color }}>{p.name}</span>
          <span style={{ fontWeight: 600 }}>{fmtShort(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

const BalanceTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const val = payload[0]?.value || 0;
  return (
    <div style={{ background: 'var(--bg-primary)', border: '1px solid var(--border-color)', borderRadius: '0.625rem', padding: '0.625rem 0.875rem', fontSize: '0.8rem', boxShadow: '0 4px 16px rgba(0,0,0,0.15)' }}>
      <div style={{ fontWeight: 700, color: 'var(--text-primary)', marginBottom: '0.25rem' }}>{label}</div>
      <div style={{ fontWeight: 700, color: val >= 0 ? '#10b981' : '#ef4444', fontSize: '0.9rem' }}>{fmtShort(val)}</div>
    </div>
  );
};

export default function Dashboard({ db }) {
  const [ingresos, setIngresos] = useState([]);
  const [gastos,   setGastos]   = useState([]);
  const [activos,  setActivos]  = useState([]);
  const [pasivos,  setPasivos]  = useState([]);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [ingSnap, gasSnap, actSnap, pasSnap] = await Promise.all([
          getDocs(collection(db, 'ingresos')),
          getDocs(collection(db, 'gastos')),
          getDocs(collection(db, 'activos')),
          getDocs(collection(db, 'pasivos')),
        ]);
        setIngresos(ingSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setGastos(gasSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setActivos(actSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setPasivos(pasSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    })();
  }, [db]);

  // ── KPI calculations ──────────────────────────────────────────────────────
  const totalIngresos      = ingresos.reduce((s, r) => s + ((r.bruto || 0) - (r.impuestos || 0)), 0);
  const totalGastos        = gastos.reduce((s, g) => s + (g.monto || 0), 0);
  const totalActivos       = activos.reduce((s, a) => s + (a.monto || 0), 0);
  const totalPasivos       = pasivos.reduce((s, p) => s + (p.monto || 0), 0);
  const patrimonioNeto     = totalActivos - totalPasivos;
  const pasivosPagoMensual = pasivos.reduce((s, p) => s + (p.pagoMensual || 0), 0);
  const balance            = totalIngresos - totalGastos - pasivosPagoMensual;
  const savingsRate        = totalIngresos > 0 ? ((totalIngresos - totalGastos) / totalIngresos) * 100 : 0;

  // ── Financial health ──────────────────────────────────────────────────────
  const debtRatio = totalIngresos > 0 ? (pasivosPagoMensual / totalIngresos) * 100 : 0;
  const emergencyMonths = totalGastos > 0
    ? (activos.filter(a => ['Efectivo','Caja de Ahorro /SAN','Dólares','CETES'].some(t => (a.tipo||'').includes(t))).reduce((s, a) => s + (a.monto || 0), 0) / totalGastos)
    : 0;

  // ── 12-month projection data ──────────────────────────────────────────────
  const chartData = useMemo(() => {
    let pasivosSim = pasivos.map(p => ({ ...p }));
    let cumulativeBalance = 0;

    return Array.from({ length: 12 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const label = `${MONTHS_ES[d.getMonth()]} ${d.getFullYear().toString().slice(2)}`;

      // Compute pasivos payments this month
      const pasivosPago = pasivosSim.reduce((s, p) => {
        if ((p.monto || 0) <= 0 || !p.pagoMensual) return s;
        return s + Math.min(p.pagoMensual, p.monto);
      }, 0);

      // Deduct payments for next month simulation
      pasivosSim = pasivosSim.map(p => ({
        ...p,
        monto: Math.max(0, (p.monto || 0) - (p.pagoMensual || 0)),
      }));

      cumulativeBalance += totalIngresos - totalGastos - pasivosPago;

      return {
        label,
        Ingresos: totalIngresos,
        Gastos: totalGastos,
        Deudas: Math.round(pasivosPago),
        balance: Math.round(cumulativeBalance),
        deficit: totalIngresos < totalGastos + pasivosPago,
      };
    });
  }, [pasivos, totalIngresos, totalGastos]);

  if (loading) return (
    <div className="page-container">
      <div className="app-loading" style={{ minHeight: '40vh' }}><div className="spinner" /></div>
    </div>
  );

  const kpis = [
    { label: 'Patrimonio Neto',  value: fmtFull(patrimonioNeto), color: patrimonioNeto >= 0 ? '#10b981' : '#ef4444', sub: `Activos ${fmtShort(totalActivos)} · Pasivos ${fmtShort(totalPasivos)}` },
    { label: 'Ingresos Neto',    value: fmtFull(totalIngresos),  color: '#3b82f6',  sub: 'Mensual en MXN, después de impuestos' },
    { label: 'Balance del Mes',  value: fmtFull(balance),        color: balance >= 0 ? '#10b981' : '#ef4444', sub: `Gastos ${fmtShort(totalGastos)} · Deudas ${fmtShort(pasivosPagoMensual)}` },
    { label: 'Tasa de Ahorro',   value: `${savingsRate.toFixed(1)}%`, color: savingsRate >= 20 ? '#10b981' : savingsRate >= 10 ? '#f59e0b' : '#ef4444', sub: savingsRate >= 20 ? 'Excelente (meta ≥20%)' : savingsRate >= 10 ? 'Mejorable (meta 20%)' : 'Por debajo de la meta' },
  ];

  const healthIndicators = [
    { label: 'Tasa de Ahorro',   value: `${savingsRate.toFixed(0)}%`,      ok: savingsRate >= 20, warn: savingsRate >= 10, tip: savingsRate >= 20 ? '✓ Excelente (>20%)' : savingsRate >= 10 ? '⚠ Mejorable (meta 20%)' : '✗ Bajo (meta 20%)' },
    { label: 'Deuda / Ingreso',  value: `${debtRatio.toFixed(0)}%`,        ok: debtRatio < 30,   warn: debtRatio < 40,   tip: debtRatio < 30 ? '✓ Saludable (<30%)' : debtRatio < 40 ? '⚠ Precaución (<40%)' : '✗ Alto (>40%)' },
    { label: 'Fondo Emergencia', value: `${emergencyMonths.toFixed(1)} m`, ok: emergencyMonths >= 6, warn: emergencyMonths >= 3, tip: emergencyMonths >= 6 ? '✓ Sólido (≥6 meses)' : emergencyMonths >= 3 ? '⚠ Mínimo (3–6)' : '✗ Insuficiente (<3)' },
  ];

  return (
    <div className="page-container">
      <h1 className="page-title">Dashboard Financiero <span style={{fontSize:'0.7rem', background:'rgba(16,185,129,0.12)', color:'#10b981', borderRadius:'0.35rem', padding:'0.2rem 0.5rem', fontWeight:700, verticalAlign:'middle'}}>MXN</span></h1>

      {/* ── KPI Cards ────────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.75rem', marginBottom: '1.25rem' }}>
        {kpis.map(({ label, value, color, sub }) => (
          <div key={label} className="card" style={{ padding: '1rem 1.125rem', borderTop: `3px solid ${color}`, borderRadius: '0.875rem', position: 'relative', overflow: 'hidden' }}>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.375rem' }}>{label}</div>
            <div style={{ fontSize: '1.2rem', fontWeight: 800, color, fontVariantNumeric: 'tabular-nums', lineHeight: 1.2 }}>{value}</div>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-light)', marginTop: '0.35rem' }}>{sub}</div>
            {/* Decorative circle */}
            <div style={{ position: 'absolute', right: -18, bottom: -18, width: 70, height: 70, borderRadius: '50%', background: color, opacity: 0.07 }} />
          </div>
        ))}
      </div>

      {/* ── Panorama 12 Meses ─────────────────────────────────────────────── */}
      <div className="card" style={{ padding: '1.25rem', marginBottom: '1rem', borderRadius: '1rem' }}>
        <div style={{ marginBottom: '0.75rem' }}>
          <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)' }}>Panorama 12 Meses</h2>
          <p style={{ margin: '0.1rem 0 0', fontSize: '0.72rem', color: 'var(--text-secondary)' }}>Compromisos mensuales (Gastos + Deudas) vs Ingresos</p>
        </div>
        <ResponsiveContainer width="100%" height={220}>
          <ComposedChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: 'var(--text-secondary)' }} axisLine={false} tickLine={false} />
            <YAxis tickFormatter={fmtShort} tick={{ fontSize: 10, fill: 'var(--text-secondary)' }} axisLine={false} tickLine={false} width={52} />
            <RTooltip content={<PanoramaTooltip />} />
            <Legend wrapperStyle={{ fontSize: '0.75rem', paddingTop: '0.5rem' }} />
            <Bar dataKey="Gastos" stackId="compromisos" fill="#3b82f6" radius={[0, 0, 0, 0]} maxBarSize={36} />
            <Bar dataKey="Deudas" stackId="compromisos" fill="#ef4444" radius={[3, 3, 0, 0]} maxBarSize={36} />
            <Line type="monotone" dataKey="Ingresos" stroke="#10b981" strokeWidth={2.5} dot={false} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* ── Balance Proyectado ────────────────────────────────────────────── */}
      <div className="card" style={{ padding: '1.25rem', marginBottom: '1.25rem', borderRadius: '1rem' }}>
        <div style={{ marginBottom: '0.75rem' }}>
          <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)' }}>Balance Proyectado</h2>
          <p style={{ margin: '0.1rem 0 0', fontSize: '0.72rem', color: 'var(--text-secondary)' }}>Acumulado en los próximos 12 meses (incluyendo liquidación de deudas)</p>
        </div>
        <ResponsiveContainer width="100%" height={160}>
          <AreaChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="balGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor="#10b981" stopOpacity={0.25} />
                <stop offset="95%" stopColor="#10b981" stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: 'var(--text-secondary)' }} axisLine={false} tickLine={false} />
            <YAxis tickFormatter={fmtShort} tick={{ fontSize: 10, fill: 'var(--text-secondary)' }} axisLine={false} tickLine={false} width={52} />
            <ReferenceLine y={0} stroke="var(--border-color)" strokeWidth={1.5} />
            <RTooltip content={<BalanceTooltip />} />
            <Area type="monotone" dataKey="balance" stroke="#10b981" strokeWidth={2.5} fill="url(#balGrad)" dot={false} name="Balance acumulado" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* ── Salud Financiera ──────────────────────────────────────────────── */}
      <div className="card" style={{ padding: '1rem', borderRadius: '1rem' }}>
        <h2 style={{ margin: '0 0 0.875rem', fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)' }}>Salud Financiera</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {healthIndicators.map(({ label, value, ok, warn, tip }) => {
            const color = ok ? '#10b981' : warn ? '#f59e0b' : '#ef4444';
            const bg    = ok ? 'rgba(16,185,129,0.08)' : warn ? 'rgba(245,158,11,0.08)' : 'rgba(239,68,68,0.08)';
            return (
              <div key={label} style={{ background: bg, borderRadius: '0.625rem', padding: '0.75rem 1rem', border: `1px solid ${color}33`, display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: color, flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', fontWeight: 600 }}>{label}</div>
                  <div style={{ fontSize: '0.78rem', color, marginTop: '0.1rem' }}>{tip}</div>
                </div>
                <div style={{ fontSize: '1.15rem', fontWeight: 800, color, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>{value}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
